type BookOfferPageProps = { onLogin: () => void };

const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/14AcN54Pl15028X0ba7wA00";

const useCases = [
  "Start a business or side venture",
  "Turn an idea into a product or service",
  "Research whether an opportunity is viable",
  "Write and publish a book",
  "Design and build a website",
  "Develop an app or software idea",
  "Solve a personal, professional or practical problem",
  "Plan an event, project or community initiative",
  "Learn a new subject or develop a skill",
  "Change career or progress at work",
  "Improve your health, routines or daily life",
  "Turn a long-held ambition into an achievable project",
];

const readerSignals = [
  "You have a dream or idea but do not know where to begin",
  "You start with enthusiasm but struggle to maintain momentum",
  "You have tried before and now question whether you can succeed",
  "Stress, procrastination, low confidence or poor focus keeps getting in the way",
  "You need knowledge, practical skills or a plan—not another slogan",
  "You are tired of following advice designed around somebody else’s life",
];

const bookBenefits = [
  ["Understand what is really stopping you", "Separate emotional barriers, practical problems and gaps in knowledge so you can work on the issue that actually matters."],
  ["Choose what success means to you", "Stop being pushed towards somebody else’s goal and define the life, project or achievement you genuinely want."],
  ["Strengthen what you need for the journey", "Develop confidence, motivation, focus and ways to manage stress or procrastination when these are holding you back."],
  ["Approach unfamiliar problems", "Use questions, research and practical thinking to understand a subject instead of relying on a single prescribed answer."],
  ["Create a route that suits you", "Turn your goal into a realistic plan based on your circumstances, strengths, resources and starting point."],
  ["Build confidence through evidence", "Take achievable action, recognise the progress you make and use that evidence to attempt something greater."],
];

const included = [
  "The Calm But Capable paperback",
  "UK postage and packaging",
  "FREE One Clear Step online workshop—valued at £98",
  "FREE 30-minute Strategy Call with Nicholas Castle—valued at £180",
];

function PurchaseCta({ light = false }: { light?: boolean }) {
  return (
    <div className="book-cta-block">
      <a className={`book-button${light ? " book-button--light" : ""}`} href={STRIPE_PAYMENT_LINK}>
        Get my free book and £278 bonus package <span aria-hidden="true">→</span>
      </a>
      <p className="book-cta-support">FREE book, One Clear Step workshop (valued at £98) and 30-minute Strategy Call (valued at £180). Only £5.99 UK postage.</p>
      <p className="book-secure-note">Secure payment through Stripe. No subscription.</p>
    </div>
  );
}

export default function BookOfferPage({ onLogin }: BookOfferPageProps) {
  return (
    <main className="public-home book-page">
      <header className="public-header">
        <a className="public-brand" href="/" aria-label="Calm But Capable home">
          <img src="/logo-white.png" alt="" className="public-logo" />
          <span>Calm But Capable</span>
        </a>
        <div className="organisation-header-actions">
          <a className="organisation-back-link" href="/">Home</a>
          <button className="public-login" type="button" onClick={onLogin}>Login</button>
        </div>
      </header>

      <section className="book-hero">
        <div className="book-hero-copy">
          <p className="book-offer-heading">
            Claim Your Book and Special Founder’s Package
          </p>
          <h1>What is stopping you being successful and achieving your dreams?</h1>
          <p className="book-lead">
            You may know that you are capable of more. You may have an idea, a problem you want to solve or a life you want to build—yet months or even years pass without the progress you expected.
          </p>
          <p className="book-hero-promise book-hero-promise--central">
            The longer you remain stuck, the easier it becomes to believe that you are the reason you have not succeeded.
          </p>
          <p className="book-hero-promise">
            But the real barrier may be something you have never been helped to identify: confidence, focus, stress, procrastination, missing knowledge, an unsuitable plan—or several of these working together.
          </p>
          <PurchaseCta />
          <blockquote className="book-testimonial book-testimonial--hero">
            <p>“I couldn’t put it down. It was so easy to read—I wanted to know what was next.”</p>
            <footer>— Sylwia, early reader</footer>
          </blockquote>
        </div>

        <div className="book-hero-aside">
          <img className="book-mockup" src="/calm-but-capable-book-mockup.webp" alt="Calm But Capable paperback by Nicholas Castle" />
          <aside className="book-use-card" aria-label="Ways to use Calm But Capable">
            <p className="public-card-label">Make it personal</p>
            <h2>What could your success look like?</h2>
            <p>Use Calm But Capable to help you:</p>
            <ul>{useCases.map((item) => <li key={item}>{item}</li>)}</ul>
            <p className="book-use-card__close">Your goal does not have to fit somebody else’s programme. CBC helps you build a route around what you want to achieve.</p>
          </aside>
        </div>
      </section>

      <section className="book-recognition">
        <div className="book-section-heading">
          <p className="public-kicker">Does this feel familiar?</p>
          <h2>You want more from life, but something keeps interrupting the journey.</h2>
        </div>
        <div className="book-reader-grid">{readerSignals.map((signal) => <div key={signal}>{signal}</div>)}</div>
        <div className="book-emotional-bridge">
          <p>Repeated false starts do more than delay a goal. They can become evidence against you. Every unfinished plan can weaken your confidence, reduce your motivation and make the next attempt feel harder than the last.</p>
          <p>Meanwhile, the idea still matters. The problem still needs solving. The life you imagined continues without you—and that is the part that hurts.</p>
        </div>
      </section>

      <section className="book-section book-introduction">
        <div>
          <p className="public-kicker">The problem with generic programmes</p>
          <h2>Most personal development starts with its answer. Calm But Capable starts with your question.</h2>
        </div>
        <div>
          <p>Many programmes decide the destination before they meet you: become more confident, improve your focus or make money by following one particular method. Everyone receives the same goal and the same route.</p>
          <p>That does not only ignore the individual. It can push large numbers of people into the same crowded market, using solutions that do not fit their abilities, circumstances or ambitions—and then leave them feeling responsible when the formula fails them.</p>
          <p className="book-strong-line">Most programmes sell you their route. Calm But Capable helps you build yours.</p>
        </div>
      </section>

      <section className="book-section book-person-first">
        <div className="book-section-heading">
          <p className="public-kicker">A person-first system</p>
          <h2>What do you want to do with your life?</h2>
          <p>Be specific. Your dream, idea, problem or project becomes the starting point.</p>
        </div>
        <div className="book-person-grid">
          <article><span>01</span><h3>Choose the destination</h3><p>You decide what success means and what you want to achieve. CBC does not choose your dream for you.</p></article>
          <article><span>02</span><h3>Identify what you need</h3><p>Explore whether you need emotional development, subject knowledge, problem-solving, research, planning or a combination.</p></article>
          <article><span>03</span><h3>Build your route</h3><p>Create achievable steps that reflect your life, resources and circumstances—then begin gathering evidence of progress.</p></article>
        </div>
      </section>

      <section className="book-section book-path">
        <div className="book-section-heading">
          <p className="public-kicker">A complete package for beginning your journey</p>
          <h2>Understand it. Plan it. Begin it.</h2>
          <p>We understand that people can read something valuable and still struggle to get started. That is why your book is supported by a workshop and a personal session.</p>
        </div>
        <ol className="book-step-grid">
          <li><span>1</span><h3>Read the book</h3><p>Understand what may be affecting your progress and discover practical tools you can apply to your own goal.</p></li>
          <li><span>2</span><h3>Use One Clear Step</h3><p>Turn what you have learned into a clear plan and choose an achievable starting point.</p></li>
          <li><span>3</span><h3>Book your free Strategy Call</h3><p>Work one-to-one with Nicholas to apply CBC to your situation and identify a realistic next step.</p></li>
        </ol>
        <PurchaseCta />
      </section>

      <section className="book-section book-benefits">
        <div className="book-section-heading">
          <p className="public-kicker">Inside Calm But Capable</p>
          <h2>More than motivation. A system for moving forward.</h2>
          <p>Motivation and confidence matter, but they are not always the whole problem. Calm But Capable helps you understand what your particular journey requires.</p>
        </div>
        <div className="book-benefit-grid">
          {bookBenefits.map(([title, description], index) => <article key={title}><span>0{index + 1}</span><h3>{title}</h3><p>{description}</p></article>)}
        </div>
      </section>

      <section className="book-section book-cycle">
        <div className="book-section-heading">
          <p className="public-kicker">Confidence can come after action</p>
          <h2>You do not have to feel confident before you begin.</h2>
          <p>Preparation creates a balanced risk. The risk allows action. Action provides experience. Experience becomes evidence—and evidence produces confidence.</p>
        </div>
        <div className="book-cycle-grid" aria-label="The continuing confidence cycle">
          <div><span>1</span><strong>Prepare</strong></div>
          <div><span>2</span><strong>Take a balanced risk</strong></div>
          <div><span>3</span><strong>Act</strong></div>
          <div><span>4</span><strong>Gain experience</strong></div>
          <div><span>5</span><strong>Recognise the evidence</strong></div>
          <div><span>6</span><strong>Grow in confidence</strong></div>
        </div>
        <p className="book-cycle-close">Then you return to the beginning with more experience and attempt something greater. This is how progress becomes continual growth.</p>
      </section>

      <section className="book-section book-offer-panel">
        <div>
          <p className="public-kicker">Everything you need to begin</p>
          <h2>Your Calm But Capable package</h2>
          <p>The value is not in receiving more information. It is in understanding yourself, choosing your direction and being supported as you take the first step.</p>
          <ul>{included.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
        <div className="book-offer-price">
          <p className="public-card-label">Complete introductory package</p>
          <p className="book-price">£5.99</p>
          <p className="book-price-note">including UK delivery</p>
          <PurchaseCta />
        </div>
      </section>

      <section className="book-section book-founder">
        <div className="organisation-founder-mark" aria-hidden="true">NC</div>
        <div>
          <p className="public-kicker">Written by Nicholas Castle</p>
          <h2>Experience of people—not assumptions about them.</h2>
          <p>Nicholas Castle is the founder of Calm But Capable. His experience as an investigator, teacher and mentor taught him to look beyond what appears obvious and search for the issue that actually matters. His own recovery from complex PTSD reinforced a belief that shapes CBC: people are often more capable than they appear—and more capable than they realise.</p>
        </div>
      </section>

      <section className="book-section book-sanctuary">
        <div>
          <p className="public-kicker">Support that can grow with you</p>
          <h2>Your first goal does not have to be your last.</h2>
        </div>
        <div>
          <p>The book, workshop and personal session help you begin. If you later want continuing support, The Sanctuary is designed to help you apply the Calm But Capable approach to new goals, subjects and problems as your journey develops.</p>
          <p>It does not replace specialist medical, legal or financial advice. It helps you think, learn, plan and move forward with greater clarity.</p>
        </div>
      </section>

      <section className="book-section book-reassurance">
        <div className="book-section-heading">
          <p className="public-kicker">Before you order</p>
          <h2>A clear, straightforward offer.</h2>
        </div>
        <div className="book-faq-grid">
          <article><h3>Does CBC tell me what my goal should be?</h3><p>No. You decide what you want to achieve. CBC helps you understand what you need and build a suitable route towards it.</p></article>
          <article><h3>Is the personal session a sales pitch?</h3><p>No. It is included to help you apply the ideas to one real issue and identify an achievable next step.</p></article>
          <article><h3>Does this guarantee success?</h3><p>No honest system can guarantee an outcome. CBC gives you a structured way to understand, prepare, act, learn and improve your next attempt.</p></article>
          <article><h3>Is this therapy or professional advice?</h3><p>No. CBC is a personal-development and planning approach. It does not replace medical, therapeutic, legal or financial advice.</p></article>
          <article><h3>What does £5.99 include?</h3><p>The paperback, UK delivery, free workshop access and a personal 30-minute Zoom session with Nicholas Castle. There is no additional postage charge.</p></article>
          <article><h3>Is there a subscription?</h3><p>No. This purchase does not start a subscription. Any future Sanctuary membership would be a separate choice.</p></article>
        </div>
      </section>

      <section className="book-final-cta">
        <p className="public-kicker">Your next step begins here</p>
        <h2>Your dream will not become a plan until you decide to begin.</h2>
        <p>Start with the Calm But Capable book. Use the workshop to shape your plan and the included personal session to begin applying it to your life.</p>
        <PurchaseCta light />
      </section>

      <footer className="organisation-footer">
        <a className="public-brand" href="/"><img src="/logo-white.png" alt="" className="public-logo" /><span>Calm But Capable</span></a>
        <p>Understanding before action. Capability before limitation.</p>
      </footer>
    </main>
  );
}
